'use client';

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useRef } from 'react';
import { Button } from '@extern/ui/button';
import { Checkbox } from '@extern/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@extern/ui/form';
import { Input } from '@extern/ui/input';
import { InputPassword, InputPasswordTooltip } from '@extern/ui/input-password';
import { Separator } from '@extern/ui/separator';
import { Text } from '@extern/ui/text';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useAuthEvents } from '@/app/(auth)/_hooks/use-auth-events';
import { AuthCard } from '@/app/(auth)/_partials/auth-card';
import { AuthGoogleButton } from '@/app/(auth)/_partials/auth-google-button';
import { AuthLastUsedButtonWrap } from '@/app/(auth)/_partials/auth-last-used-badge';
import { useIsLabsArrival } from '@/app/(auth)/_partials/labs-arrival/labs-arrival-context';
import { PORTFOLIO_LOCKED_EMAIL_INPUT_PROPS, PortfolioLockedEmailHint } from '@/app/(auth)/_partials/portfolio-arrival/portfolio-locked-email';
import { PORTFOLIO_ARRIVAL_SUBMIT_LABEL, usePortfolioArrivalFields } from '@/app/(auth)/_partials/portfolio-arrival/use-portfolio-arrival-fields';
import { prefilledEmail, prefilledNewsletter } from '@/app/(auth)/_utils/prefilled-email';
import { emailSchema, passwordSchema } from '@/infra/schemas/auth';
import { cn } from '@/utils/cn';
import { getAllowlistedSearchParams } from '@/utils/get-allowlisted-search-params';
import { useLastAuthenticationStrategy } from '../../_hooks/use-last-authentication-strategy';
import { useSignUp } from '../_hooks/use-sign-up';
import { useSignUpFlowStore } from '../_hooks/use-sign-up-flow-store';
const FormSchema = z.object({
  firstName: z.string().min(2, {
    message: 'First name is required'
  }),
  lastName: z.string().min(2, {
    message: 'Last name is required'
  }),
  emailAddress: emailSchema,
  password: passwordSchema
});
export function SignUpFormControl() {
  const $ = _c(85);
  const isLabsArrival = useIsLabsArrival();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = prefilledEmail(searchParams);
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const hasPrefilledEmail = t0 !== "";
  const {
    onSignUpSubmit,
    onSignUpWithGoogle,
    isPending,
    isGoogleLoading
  } = useSignUp();
  const {
    trackSignUpChoiceClicked
  } = useAuthEvents();
  const lastAuthenticationStrategy = useLastAuthenticationStrategy();
  const newsletter = useSignUpFlowStore(_temp);
  const onChangeFlow = useSignUpFlowStore(_temp2);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = zodResolver(FormSchema);
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== searchParams) {
    t2 = prefilledEmail(searchParams);
    $[3] = searchParams;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = {
      resolver: t1,
      defaultValues: {
        firstName: "",
        lastName: "",
        emailAddress: t2,
        password: ""
      },
      mode: "onChange"
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const form = useForm(t3);
  const portfolio = usePortfolioArrivalFields(form.setValue, form.getValues);
  const layoutCarriesHeading = isLabsArrival || portfolio.isPortfolioArrival;
  const appliedNewsletterChoice = useRef(false);
  let t4;
  let t5;
  if ($[7] !== onChangeFlow || $[8] !== searchParams) {
    t4 = () => {
      if (appliedNewsletterChoice.current) {
        return;
      }
      appliedNewsletterChoice.current = true;
      const choice = prefilledNewsletter(searchParams);
      if (choice !== null) {
        onChangeFlow({
          newsletter: choice
        });
      }
    };
    t5 = [searchParams, onChangeFlow];
    $[7] = onChangeFlow;
    $[8] = searchParams;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useLayoutEffect(t4, t5);
  let t6;
  if ($[11] !== newsletter || $[12] !== onSignUpSubmit || $[13] !== trackSignUpChoiceClicked) {
    t6 = function handleSubmit(values) {
      trackSignUpChoiceClicked("email", newsletter);
      onSignUpSubmit({
        ...values,
        newsletter
      });
    };
    $[11] = newsletter;
    $[12] = onSignUpSubmit;
    $[13] = trackSignUpChoiceClicked;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleSubmit = t6;
  const t7 = portfolio.isPortfolioArrival ? "w-full" : undefined;
  let t8;
  if ($[15] !== form || $[16] !== handleSubmit) {
    t8 = form.handleSubmit(handleSubmit);
    $[15] = form;
    $[16] = handleSubmit;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== layoutCarriesHeading) {
    t9 = layoutCarriesHeading ? null : <AuthCard.Header><AuthCard.Title>Start Your Career Journey with Extern</AuthCard.Title><AuthCard.Description>Gain access to real-world projects, hands-on experience, and a community of professionals.</AuthCard.Description></AuthCard.Header>;
    $[18] = layoutCarriesHeading;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== form.control || $[21] !== isLabsArrival || $[22] !== portfolio.namesKnown) {
    t10 = portfolio.namesKnown ? null : <div className={isLabsArrival ? "grid grid-cols-2 items-start gap-3" : "contents"}><FormField control={form.control} name="firstName" render={_temp3} /><FormField control={form.control} name="lastName" render={_temp4} /></div>;
    $[20] = form.control;
    $[21] = isLabsArrival;
    $[22] = portfolio.namesKnown;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== hasPrefilledEmail || $[25] !== isLabsArrival || $[26] !== portfolio.lockedEmail) {
    t11 = t12 => {
      const {
        field: field_1
      } = t12;
      return <FormItem><FormLabel className="flex items-baseline justify-between">Email{portfolio.lockedEmail ? <PortfolioLockedEmailHint /> : null}{isLabsArrival && hasPrefilledEmail ? <span className="font-normal text-role-on-background-subtle text-xs">from your lab</span> : null}</FormLabel><FormControl><Input placeholder="email@service.com" {...field_1} {...portfolio.lockedEmail ? PORTFOLIO_LOCKED_EMAIL_INPUT_PROPS : {}} /></FormControl><FormMessage /></FormItem>;
    };
    $[24] = hasPrefilledEmail;
    $[25] = isLabsArrival;
    $[26] = portfolio.lockedEmail;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== form.control || $[29] !== t11) {
    t12 = <FormField control={form.control} name="emailAddress" render={t11} />;
    $[28] = form.control;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== form.control) {
    t13 = <FormField control={form.control} name="password" render={_temp5} />;
    $[31] = form.control;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== onChangeFlow) {
    t14 = checked => onChangeFlow({
      newsletter: checked === true
    });
    $[33] = onChangeFlow;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== newsletter || $[36] !== t14) {
    t15 = <div className="flex gap-2"><Checkbox checked={newsletter} label="I want exclusive invites and opportunities at Extern" onCheckedChange={t14} /></div>;
    $[35] = newsletter;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t10 || $[39] !== t12 || $[40] !== t13 || $[41] !== t15) {
    t16 = <AuthCard.Content><div className="grid gap-4">{t10}{t12}{t13}{t15}</div></AuthCard.Content>;
    $[38] = t10;
    $[39] = t12;
    $[40] = t13;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <br />;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Link className="underline" href="https://www.extern.com/terms-of-service" target="_blank">Terms</Link>;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="text-center font-medium text-gray-dark text-xs">By signing up, you agree to{t17}Extern's{" "}{t18}{" "}and{" "}<Link className="underline" href="https://www.extern.com/privacy-policy" target="_blank">Privacy Policy</Link>.</div>;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  const t20 = isLabsArrival || portfolio.isPortfolioArrival ? PORTFOLIO_ARRIVAL_SUBMIT_LABEL : "Sign up with Email";
  let t21;
  if ($[46] !== isPending || $[47] !== t20) {
    t21 = <Button className="w-full" disabled={isPending} isLoading={isPending} type="submit">{t20}</Button>;
    $[46] = isPending;
    $[47] = t20;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== lastAuthenticationStrategy || $[50] !== t21) {
    t22 = <AuthLastUsedButtonWrap matchStrategy="password" strategy={lastAuthenticationStrategy}>{t21}</AuthLastUsedButtonWrap>;
    $[49] = lastAuthenticationStrategy;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Separator />;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  const t24 = layoutCarriesHeading ? "bg-role-surface" : "bg-white";
  let t25;
  if ($[53] !== t24) {
    t25 = cn("absolute mx-auto px-4 text-gray-dark text-sm", t24);
    $[53] = t24;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== t25) {
    t26 = <div className="relative flex w-full items-center justify-center p-3">{t23}<span className={t25}>or</span></div>;
    $[55] = t25;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== newsletter || $[58] !== onSignUpWithGoogle || $[59] !== trackSignUpChoiceClicked) {
    t27 = () => {
      trackSignUpChoiceClicked("google", newsletter);
      onSignUpWithGoogle(newsletter).catch(_temp6);
    };
    $[57] = newsletter;
    $[58] = onSignUpWithGoogle;
    $[59] = trackSignUpChoiceClicked;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== isGoogleLoading || $[62] !== t27) {
    t28 = <AuthGoogleButton isLoading={isGoogleLoading} label="Sign up with Google" onClick={t27} />;
    $[61] = isGoogleLoading;
    $[62] = t27;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== searchParams) {
    t29 = getAllowlistedSearchParams(searchParams);
    $[64] = searchParams;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  const t30 = `/sign-in?${t29}`;
  let t31;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <span className="text-blue-dark hover:underline">Sign in</span>;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] !== t30) {
    t32 = <Text body="lg" className="text-center font-medium">Already have an account? <Link href={t30}>{t31}</Link></Text>;
    $[67] = t30;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== t22 || $[70] !== t26 || $[71] !== t28 || $[72] !== t32) {
    t33 = <AuthCard.Footer>{t19}{t22}{t26}{t28}{t32}</AuthCard.Footer>;
    $[69] = t22;
    $[70] = t26;
    $[71] = t28;
    $[72] = t32;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== t16 || $[75] !== t33 || $[76] !== t9) {
    t34 = <AuthCard>{t9}{t16}{t33}</AuthCard>;
    $[74] = t16;
    $[75] = t33;
    $[76] = t9;
    $[77] = t34;
  } else {
    t34 = $[77];
  }
  let t35;
  if ($[78] !== t34 || $[79] !== t7 || $[80] !== t8) {
    t35 = <form className={t7} onSubmit={t8}>{t34}</form>;
    $[78] = t34;
    $[79] = t7;
    $[80] = t8;
    $[81] = t35;
  } else {
    t35 = $[81];
  }
  let t36;
  if ($[82] !== form || $[83] !== t35) {
    t36 = <Form {...form}>{t35}</Form>;
    $[82] = form;
    $[83] = t35;
    $[84] = t36;
  } else {
    t36 = $[84];
  }
  return t36;
}
function _temp6() {}
function _temp5(t0) {
  const {
    field: field_2
  } = t0;
  return <FormItem><FormLabel className="flex items-center gap-2">Create a password<InputPasswordTooltip value={field_2.value} /></FormLabel><FormControl><InputPassword autoCapitalize="none" autoComplete="password" autoCorrect="off" data-cy="password" id="password" key="password" placeholder="Type in your password" type="password" {...field_2} /></FormControl><FormMessage /></FormItem>;
}
function _temp4(t0) {
  const {
    field: field_0
  } = t0;
  return <FormItem><FormLabel>Last name</FormLabel><FormControl><Input placeholder="Last name" {...field_0} /></FormControl><FormMessage /></FormItem>;
}
function _temp3(t0) {
  const {
    field
  } = t0;
  return <FormItem><FormLabel>First name</FormLabel><FormControl><Input placeholder="First name" {...field} /></FormControl><FormMessage /></FormItem>;
}
function _temp2(s_0) {
  return s_0.onChange;
}
function _temp(s) {
  return s.newsletter;
}